import { Component, OnInit, Input, ChangeDetectionStrategy, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { MatSnackBar } from '@angular/material';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { Store, Select, Actions, ofActionDispatched } from '@ngxs/store';
import {
    SetPublishDimensions,
    GetPublishIFrame,
    GetPublishURL,
    GetPublishURLSuccess,
    GetPublishIFrameSuccess,
    GetPublishURLFail,
    GetPublishIFrameFail,
    SetPublishing,
    GetPublishImageCode,
    GetPublishImageCodeSuccess,
    GetPublishImageCodeFail,
    SavePublishImage,
    SavePublishImageSuccess,
    SavePublishImageFail
} from '../../state/publish.actions';
import { PublishState } from '../../state/publish.state';
import { Observable, Subject, of } from 'rxjs';
import { ScreenDimensions } from 'src/app/shared/models/screen.dimensions.model';
import { ColorModel } from 'src/app/edit/models/color.model';
import { CameraModel } from 'src/app/engine/models/camera.model';
import { WidgetOptions } from '../../models/widget-options.model';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { debounceTime, takeUntil, flatMap } from 'rxjs/operators';
import { OrganizationsState } from 'src/app/organizations/state/organizations.state';
import { PublishService } from '../../publish.service';
import { StorageService, StorageItemEnum } from 'src/app/shared/storage.service';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { ClipboardService } from 'src/app/shared/clipboard.service';
import { DialogService } from 'src/app/dialogs/dialog.service';
import { PublishWidgetComponent } from '../publish-widget/publish-widget.component';
import { ScreenModel } from 'src/app/shared/models/screen.model';
import { ImageTypeEnum } from 'src/app/shared/image.service';
import { BackgroundModel } from 'src/app/engine/models/background.model';
import { get } from 'lodash';

const snapshotSmall: ScreenModel = {
  'title': 'Small',
  'type': 'desktop',
  'dimensions': {
    'width': 480,
    'height': 320
  },
  'orientation': 'horizontal'
};

const snapshotMedium: ScreenModel = {
  'title': 'Medium',
  'type': 'desktop',
  'dimensions': {
    'width': 700,
    'height': 550
  },
  'orientation': 'horizontal'
};

const snapshotLarge: ScreenModel = {
  'title': 'Large',
  'type': 'desktop',
  'dimensions': {
    'width': 1920,
    'height': 1200
  },
  'orientation': 'horizontal'
};

const snapshotMax: ScreenModel = {
  'title': 'Maximum',
  'type': 'desktop',
  'dimensions': {
    'width': 2560,
    'height': 1440
  },
  'orientation': 'horizontal'
};

@Component({
  selector: 'app-publish',
  templateUrl: './publish.component.html',
  styleUrls: ['./publish.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PublishComponent implements OnInit, OnDestroy {

  // snapshot state
  minSnapshotDimensions: ScreenDimensions = {
    width: 300,
    height: 300
  };
  maxSnapshotDimensions: ScreenDimensions = {
    width: 2560,
    height: 1440
  };
  snapshotSizes: ScreenModel[] = [snapshotSmall, snapshotMedium, snapshotLarge, snapshotMax];
  selectedSnapshotSize = snapshotMedium;
  snapshotBGColor: BackgroundModel;

  @Input() model: BookmarkModel;

  @Select(PublishState.getDimensions)
  dimensions$: Observable<ScreenDimensions>;

  @Select(PublishState.getMinDimensions)
  minDimensions$: Observable<ScreenDimensions>;

  @ViewChild(PublishWidgetComponent)
  publishWidget: PublishWidgetComponent;

  get widgetOptions() {
    return this.mode === '2d' ? null : this.form.value.options;
  }

  get rotateEnabled() {
    const rotate = this.form.get('rotate').value;
    return rotate.enabled;
  }

  get uiToolsGrouped() {
    const { uiToolsDisplay } = this.form.value.options;
    return uiToolsDisplay !== 'primary';
  }

  get mode(): '2d' | '3d' {
    return this.formModeGroup.get('mode').value;
  }

  get widgetWidth(): number {
    if (this.mode === '2d') {
      return this.selectedSnapshotSize.dimensions.width;
    } else {
      return this.store.selectSnapshot(PublishState.getDimensions).width;
    }
  }

  get widgetHeight(): number {
    if (this.mode === '2d') {
      return this.selectedSnapshotSize.dimensions.height;
    } else {
      return this.store.selectSnapshot(PublishState.getDimensions).height;
    }
  }

  get widgetBGColor(): BackgroundModel {
    if (this.mode === '2d') {
      return this.snapshotBGColor;
    } else {
      return this.bgColor;
    }
  }

  get widgetScalable(): boolean {
    return this.mode === '2d';
  }

  formModeGroup: FormGroup;
  form: FormGroup;  
  formSnapshot: FormGroup;

  camera: CameraModel;
  bgColor: BackgroundModel;

  private ngUnsubscribe = new Subject();

  // optional: default options
  private defaultWidgetOptions: WidgetOptions = {
    ...new WidgetOptions(),
    loadRotate: 10
  };

  constructor(
    private store: Store,
    private actions: Actions,
    private publishService: PublishService,
    private storageService: StorageService,
    private clipboardService: ClipboardService,
    private dialogService: DialogService,
    private snackBar: MatSnackBar,
    private fb: FormBuilder,
    private ref: ChangeDetectorRef
  ) {

    const widgetOptions = this.getWidgetOptions();
    const dimensions = this.store.selectSnapshot(PublishState.getDimensions);
    const minDimensions = this.store.selectSnapshot(PublishState.getMinDimensions);
    const { button, handHint } = widgetOptions.initial;
    const { alert, overlay } = widgetOptions.disableScroll;

    // mode selection form
    this.formModeGroup = this.fb.group({
      mode: ['3d', Validators.required]
    });

    // snapshot form
    const snapshotDimensions = this.selectedSnapshotSize.dimensions;
    this.formSnapshot = this.fb.group({
      dimensions: this.fb.group({
        width: [
          snapshotDimensions.width,
          [Validators.required, Validators.min(this.minSnapshotDimensions.width), Validators.max(this.maxSnapshotDimensions.width)]
        ],
        height: [
          snapshotDimensions.height,
          [Validators.required, Validators.min(this.minSnapshotDimensions.height), Validators.max(this.maxSnapshotDimensions.height)]
        ]
      })
    });

    // widget 3D form
    this.form = this.fb.group({
      dimensions: this.fb.group({
        width: [dimensions.width, [Validators.min(minDimensions.width)]],
        height: [dimensions.height, [Validators.min(minDimensions.height)]]
      }),
      options: this.fb.group(widgetOptions),
      rotate: this.fb.group({
        enabled: widgetOptions.loadRotate !== null,
        value: widgetOptions.loadRotate
      }),
      initial: this.fb.group({
        enabled: button || handHint,
        mode: button ? 'button' : (handHint ? 'handHint' : '')
      }),
      disableScroll: this.fb.group({
        enabled: alert || overlay,
        mode: alert ? 'alert' : (overlay ? 'overlay' : '')
      })
    });

    // set color
    const colors = get(widgetOptions, 'background.colors');
    if (colors) {
      const color: ColorModel = {
        red: colors[0],
        green: colors[1],
        blue: colors[2],
        alpha: colors.length > 3 ? colors[3] : 1
      };
      this.bgColor = this.getBackgroundFromColor(color);
    }

    // subscribe to url/code generation
    this.actions
      .pipe(
        ofActionDispatched(GetPublishURLSuccess),
        flatMap((response: GetPublishURLSuccess) => this.copyToClipboard(response.url)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((copied) => {
        this.snackBar.open(copied ? 'Successfully copied URL to clipboard.' : 'Unable to copy URL to clipboard.');
      });
    this.actions
      .pipe(
        ofActionDispatched(GetPublishURLFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.snackBar.open(`Unable to generate URL.`);
      });

    this.actions
      .pipe(
        ofActionDispatched(GetPublishIFrameSuccess),
        flatMap((response: GetPublishIFrameSuccess) => this.copyToClipboard(response.embed)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((copied) => {
        this.snackBar.open(copied ? 'Successfully copied embed code to clipboard.' : 'Unable to copy embed code to clipboard.');
      });
    this.actions
      .pipe(
        ofActionDispatched(GetPublishIFrameFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.snackBar.open(`Unable to generate embed code.`);
      });

    // generate image url
    this.actions
      .pipe(
        ofActionDispatched(SavePublishImageSuccess),
        flatMap((response: SavePublishImageSuccess) => this.copyToClipboard(response.imageURL)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((copied) => {
        this.snackBar.open(copied ? 'Successfully copied image URL to clipboard.' : 'Unable to copy image URL to clipboard.');
      });
    this.actions
      .pipe(
        ofActionDispatched(SavePublishImageFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.snackBar.open(`Unable to generate image URL.`);
      });

    // generate image code
    this.actions
      .pipe(
        ofActionDispatched(GetPublishImageCodeSuccess),
        flatMap((response: GetPublishImageCodeSuccess) => this.copyToClipboard(response.embed)),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((copied) => {
        this.snackBar.open(copied ? 'Successfully copied image embed code to clipboard.' : 'Unable to copy image embed code to clipboard.');
      });
    this.actions
      .pipe(ofActionDispatched(GetPublishImageCodeFail),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        this.snackBar.open(`Unable to generate embed code.`);
      });
  }

  ngOnInit() {
    this.setupWidgetForm();
    this.setupSnapshotForm();

    this.store.dispatch(new SetPublishing(true));
  }

  ngOnDestroy() {
    this.store.dispatch(new SetPublishing(false));

    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  setupWidgetForm() {
    const optionsGroup = this.form.get('options');
    const dimensionsGroup = this.form.get('dimensions');
    const rotateGroup = this.form.get('rotate');
    const initialGroup = this.form.get('initial');
    const disableScrollGroup = this.form.get('disableScroll');

    // rotate params
    const rotateEnabledField = rotateGroup.get('enabled');
    const rotateValueField = rotateGroup.get('value');

    // set rotate defaults when toggling rotate enabled
    rotateEnabledField.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        if (enabled) { // set default widget rotate
          const rotateValue = typeof (rotateValueField.value) === 'number' ? rotateValueField.value : this.defaultWidgetOptions.loadRotate;
          rotateValueField.patchValue(rotateValue);
        } else { // reset widget rotate
          optionsGroup.patchValue({
            loadRotate: null
          });
        }
      });

    // push rotate changes to widget options
    rotateValueField.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((value) => {
        optionsGroup.patchValue({
          loadRotate: value
        });
      });

    // sync initial params
    initialGroup.get('enabled').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        initialGroup.patchValue({
          mode: enabled ? 'button' : ''
        });
      });
      
    initialGroup.get('mode').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((mode) => {
        initialGroup.patchValue({ enabled: !!mode }, { emitEvent: false });
        optionsGroup.patchValue({
          initial: {
            button: mode === 'button',
            handHint: mode === 'handHint'
          }
        });
      });

    // sync scroll params
    disableScrollGroup.get('enabled').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((enabled) => {
        disableScrollGroup.patchValue({
          mode: enabled ? 'alert' : ''
        });
      });

    disableScrollGroup.get('mode').valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((mode) => {
        disableScrollGroup.patchValue({ enabled: !!mode }, { emitEvent: false });
        optionsGroup.patchValue({
          disableScroll: {
            alert: mode === 'alert',
            overlay: mode === 'overlay'
          }
        });
      });

    // sync dimensions
    dimensionsGroup.valueChanges
      .pipe(
        debounceTime(300),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((dimensions) => {
        this.setScreenDimensions(dimensions);
      });
      
    this.dimensions$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((dimensions) => {
        dimensionsGroup.setValue(dimensions, { emitEvent: false });
      });

    optionsGroup.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((options) => {
        // persist options
        this.writeSavedOptions(options);
      });
  }

  setupSnapshotForm() {
    const formSnapshotDimensions = this.formSnapshot.get('dimensions');

    formSnapshotDimensions.valueChanges
      .pipe(
        debounceTime(500),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(() => {
        // validation
        const { width, height } = formSnapshotDimensions.value;
        if (formSnapshotDimensions.valid) {
          // find target dimensions or custom
          const foundSnap = this.snapshotSizes.find((snap) => snap.dimensions.width === width && snap.dimensions.height === height);

          this.selectedSnapshotSize = {
            ...this.selectedSnapshotSize,
            title: foundSnap ? foundSnap.title : 'Custom',
            dimensions: {
              width,
              height
            }
          };
          // mark changes
          this.ref.markForCheck();
        }
      });
  }

  setUIToolsGrouped(grouped: boolean) {
    const uiToolsDisplay = grouped ? '' : 'primary';
    this.form.patchValue({
      options: {
        uiToolsDisplay: uiToolsDisplay
      }
    });
  }

  setScreenDimensions(dimensions: ScreenDimensions) {
    this.store.dispatch(new SetPublishDimensions(dimensions));
  }

  handleGenerateIFrame() {
    this.generateIFrameCode();
  }

  handleGenerateURL() {
    this.generateURL();
  }

  handleCopyImageURL() {
    this.generateImageURL();
  }

  handleGenerateImage() {
    this.generateImageCode();
  }

  generateIFrameCode() {
    const widgetOptions = this.form.value.options;
    this.store.dispatch(new GetPublishIFrame(this.model, widgetOptions, this.camera));
  }

  generateURL() {
    const widgetOptions = this.form.value.options;
    this.store.dispatch(new GetPublishURL(this.model, widgetOptions, this.camera));
  }

  generateImageURL() {
    this.publishWidget.takeSnapshot()
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((imageData) => {
        this.store.dispatch(new SavePublishImage(imageData));
      });
  }

  generateImageCode() {
    this.publishWidget.takeSnapshot()
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((imageData) => {
        const widgetOptions = new WidgetOptions(); // use default options
        this.store.dispatch(new GetPublishImageCode(this.model, imageData, widgetOptions, this.camera));
      });
  }

  handleSnapshotBGColorClick(color: ColorModel) {
    this.snapshotBGColor = this.getBackgroundFromColor(color);
  }

  handleSnapshotSizeClick(screen: ScreenModel) {
    this.selectedSnapshotSize = {
      ...screen,
    };
    this.formSnapshot.patchValue({
      dimensions: {
        ...screen.dimensions
      }
    });
  }

  handleDownloadSnapClick() {
    this.publishWidget.takeSnapshot()
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((imageData) => {
        this.downloadImage(imageData);
      });
  }

  handleCameraUpdated(camera: CameraModel) {
    this.camera = camera;
  }

  copyToClipboard(copyText: string): Observable<boolean> {
    return this.clipboardService.copyToClipboard(copyText)
      .pipe(
        flatMap((copied) => {
          if (copied) {
            return of(copied);
          } else {
            return this.dialogService.openClipboardDialog(copyText)
              .afterClosed();
          }
        })
      );
  }

  formatScreenSizeText(screen: ScreenModel): string {
    const { title, dimensions } = screen;
    return `${title} (${dimensions.width} x ${dimensions.height})`;
  }

  private getBackgroundFromColor(color: ColorModel): BackgroundModel {
    const colors = color ? [color.red, color.green, color.blue, color.alpha] : null;
    if (colors) {
      return new BackgroundModel(
        [...colors, ...colors],
        'radial',
        [0, 100]
      );
    } else {
      return null;
    }
  }

  private getWidgetOptions(): WidgetOptions {

    // read persisted options
    const storedOptions = this.readSavedOptions();

    // read organization settings
    const orgWidgetOptions = this.store.selectSnapshot(OrganizationsState.getWidgetOptions);

    // apply saved settings
    if (storedOptions) {
      const userOptions = this.publishService.extendWidgetOptions(new WidgetOptions(), storedOptions);
      return {
        ...userOptions,
        // !important: disable user background, expect from organization
        background: orgWidgetOptions ? orgWidgetOptions.background : null
      };
    } else {
      return orgWidgetOptions || new WidgetOptions();
    }
  }

  private readSavedOptions(): any {    
    // read persisted options
    const storedOptions = this.storageService.getLocalJSON(StorageItemEnum.WidgetOptions);
    if (storedOptions) {
      const key = this.getStorageKey();
      return storedOptions[key];
    } else {
      return null;
    }
  }

  private writeSavedOptions(options: WidgetOptions) {
    // modify existing entry
    const storedOptions = this.storageService.getLocalJSON(StorageItemEnum.WidgetOptions) || {};
    const key = this.getStorageKey();
    storedOptions[key] = options;
    this.storageService.setLocalItem(StorageItemEnum.WidgetOptions, storedOptions);
  }

  // get unique key for widget settings
  private getStorageKey(): string {
    return this.store.selectSnapshot(AuthState.getUserProfile).eid;
  }

  private downloadImage(imageData: string) {
    const title = this.model.title || 'snapshot';
    const normalizedTitle = title.replace(/ /g, '_').replace(/\W/g, '');
    const fileName = `${normalizedTitle}.jpeg`;

    this.publishService.exportImage(imageData, ImageTypeEnum.JPEG, fileName)
      .pipe(
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((success) => {
        this.snackBar.open(success ? `Successfuly download image` : `Unable to download image.`);
      });
  }

}
